import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, UploadCloud, Trash2, Copy, Folder } from "lucide-react";

/**
 * MediaLibrary — grid view of uploaded assets with search, folder filter,
 * upload, and delete.
 */
export default function MediaLibrary() {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setBusy(true);
    const params = new URLSearchParams();
    if (folder) params.set("folder", folder);
    if (q) params.set("q", q);
    api.get(`/admin/media?${params.toString()}`)
      .then((r) => setAssets(r.data))
      .catch(() => toast.error("Could not load media"))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(); }, [folder, q]);
  useEffect(() => { api.get("/admin/media/folders").then((r) => setFolders(r.data)).catch(() => {}); }, []);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder || "uploads");
      try {
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    toast[fail ? "warning" : "success"](`Uploaded ${ok}/${ok + fail} files`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this asset? It will be hidden from the library.")) return;
    await api.delete(`/admin/media/${id}`);
    load();
  };

  const copy = (url) => {
    const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(full);
    toast.success("URL copied");
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-1">All uploaded assets — reusable across Cars, Articles and Reviews.</p>
        </div>
        <div className="flex gap-2 items-center">
          <input ref={fileRef} data-testid="media-upload-input" type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => upload(e.target.files)} />
          <button data-testid="media-upload-trigger" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <UploadCloud className="w-4 h-4"/>}
            {uploading ? "Uploading" : "Upload"}
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input data-testid="media-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search filename or alt…"
            className="h-10 pl-9 pr-3 rounded-full border border-[#E5E7EB] bg-white text-sm outline-none focus:border-blue-400 w-64"/>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="folder-all" onClick={() => setFolder("")}
            className={`h-9 px-3 rounded-full border border-[#E5E7EB] text-xs ${folder === "" ? "bg-[#2563EB] text-white" : "bg-white"}`}>All</button>
          {folders.map((f) => (
            <button key={f} data-testid={`folder-${f}`} onClick={() => setFolder(f)}
              className={`h-9 px-3 rounded-full border border-[#E5E7EB] text-xs inline-flex items-center gap-1 ${folder === f ? "bg-[#2563EB] text-white" : "bg-white"}`}>
              <Folder className="w-3 h-3"/> {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {busy ? (
          <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : assets.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground text-sm">No media yet. Upload your first image.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {assets.map((a) => (
              <div key={a.id} data-testid={`media-item-${a.id}`} className="card-premium overflow-hidden">
                <div className="aspect-[4/3] bg-[#F3F6FA] overflow-hidden">
                  <img src={a.url} alt={a.alt || a.original_filename} loading="lazy" className="w-full h-full object-cover"/>
                </div>
                <div className="p-3">
                  <div className="text-xs font-medium truncate" title={a.original_filename}>{a.original_filename}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{a.folder} · {(a.size/1024).toFixed(0)} KB</div>
                  <div className="mt-2 flex gap-1.5">
                    <button data-testid={`media-copy-${a.id}`} onClick={() => copy(a.url)}
                      className="flex-1 h-8 rounded-lg border border-[#E5E7EB] text-xs bg-white hover:bg-[#F3F6FA] inline-flex items-center justify-center gap-1">
                      <Copy className="w-3 h-3"/> Copy
                    </button>
                    <button data-testid={`media-delete-${a.id}`} onClick={() => del(a.id)}
                      className="h-8 w-8 rounded-lg border border-[#E5E7EB] text-rose-600 bg-white hover:bg-rose-50 grid place-items-center">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
